import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

import { ref, computed, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { onClickOutside, useClipboard } from '@vueuse/core'
import MemberDrawerContent from '@/components/drawers/MemberDrawer/MemberDrawerContent/index.vue'
import useAppointmentLinks from '@/composables/useAppointmentLinks'
import i18n from '@/scripts/app-configs/i18n-config'
import { getProtocolInfo } from '@/components/drawers/MemberDrawer/utils/patient-protocol'
import { $date, $http, $notify } from '@/plugins'
import { triggers } from '@/scripts/global-modals-commands'
import { segment } from '@/utils/segment'
import { EVENT_KEYS } from '@/utils/segment/segment-events'
import store from '@/store'
import { FeedEventSource, MemberEventSource } from '@/utils/segment/segment.types'
import type { MemberDrawerSummary, Context } from '@/components/drawers/MemberDrawer/types/member-drawer.types.ts'
import { useRoute } from 'vue-router'

interface DrawerPayload {
  memberUuid: string
  masterTaskId: string
  memberStatus: string
  source?: FeedEventSource | MemberEventSource
  taskListSegmentEvent: { uuid: string, type: string }[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    modalOpen: { type: Boolean },
    payload: {}
  },
  setup(__props: any) {

const props = __props

const route = useRoute()
const drawerRef = ref(null)
const autoHide = ref(false)
const isNotesToggled = ref(false)

const memberUuid = computed(() => props.payload?.memberUuid)
const masterTaskId = computed(() => props.payload?.masterTaskId)
const memberStatus = computed(() => props.payload?.memberStatus)
const source = computed(() => props.payload?.source || FeedEventSource.SIDEBAR)
const taskListSegmentEvent = computed(() => props.payload?.taskListSegmentEvent)

const { generateAppointmentLink } = useAppointmentLinks(memberUuid)
const { copy: copyToClipFn } = useClipboard({ legacy: true })

const therapistId = computed(() => store.getters['user/getBasicProfile'].id)

const { data: memberDrawerData, isLoading, error } = useQuery<MemberDrawerSummary>({
  queryKey: ['summary-sidebar', memberUuid],
  queryFn: async () => {
    const { data } = await $http('patient/fetchPatientSummarySidebarInfo', memberUuid.value)

    return data
  },
  enabled: () => !!memberUuid.value,
  retry: 0,
})

const memberProfile = computed(() => memberDrawerData.value?.profile)

const age = computed(() => {
  const birthdate = memberProfile.value?.birthdate

  return birthdate && $date(birthdate).age()
})

const protocolInfo = computed(() => {
  if (!memberDrawerData.value?.protocol) return undefined

  return getProtocolInfo(memberDrawerData.value.protocol)
})

const contacts = computed(() => {
  if (!memberDrawerData.value) return []

  const phoneNumber = memberProfile.value?.phone
    ? `${memberProfile.value?.phone.prefix} ${memberProfile.value?.phone?.number}`
    : ''

  const phoneWarningMessage = memberProfile.value?.phone?.can_receive_sms === false
    ? i18n.t('webchat_sidebar_profile.number_not_eligible_sms')
    : undefined

  return [
    { label: 'PROFILE.PERSONAL_INFORMATION.PREFERRED_NAME', content: memberProfile.value?.preferred_name, showCopyButton: true },
    { label: 'COMMON.EMAIL', content: memberProfile.value?.email, showCopyButton: true },
    { label: 'copy_1289', content: phoneNumber, showCallButton: true, showCopyButton: true, warningMessage: phoneWarningMessage },
  ]
})

const patientData = computed(() => ({
  user_id: memberProfile.value?.user_id,
  firstname: memberProfile.value?.firstname,
  lastname: memberProfile.value?.lastname,
  vip: !!memberProfile.value?.is_vip_account,
  highRisk: !!memberProfile.value?.patient_is_high_risk,
  name: `${memberProfile.value?.firstname} ${memberProfile.value?.lastname}`,
  gender: memberProfile.value?.gender,
  picture: memberProfile.value?.picture,
  masterTaskId: masterTaskId.value,
  source: source.value,
}))

const changeAttentionLevel = (level: string) => {
  const patientId = memberProfile.value?.user_id
  const body = { attention_level: level, origin: 'clinical_portal' }

  $http('patient/profile/setAttentionLevel', patientId, { body })
}
const closeModal = () => triggers.toggleMemberSidebar()

const openUi = ({ context }: {context: Context}) => {
  switch (context) {
    case 'notes':
      triggers.toggleNotes(patientData.value)
      isNotesToggled.value = true
      break
    case 'sword-desk':
      triggers.openSwordDesk(
        {
          memberEmail: memberProfile.value?.email,
          memberAccountUuid: memberProfile.value?.member_account_id,
          memberProgramUuid: memberUuid.value,
          memberStatus: memberStatus.value,
          masterTaskId: masterTaskId.value,
          createTicketSource: FeedEventSource.SIDEBAR,
        })

      return
    default:
      console.warn('[patient-sidebar] Requested unsupported option')
  }
  closeModal()
}

const requestAppointmentLink = async (appointmentType: string, sendBySms = false) => {
  try {
    const { appointmentUrl } = await generateAppointmentLink(appointmentType, sendBySms)

    return Promise.resolve(appointmentUrl)
  } catch (e) {

    return Promise.reject(e)
  }
}

const copyToClipboard = async ({ context, content }: { context: Context, content?: string }) => {
  const contextsAlreadyCopied = ['patient-id']
  const shouldCopy = !contextsAlreadyCopied.includes(context)

  let contentToCopy = content

  if (!contentToCopy) {
    // since not content, get context to get the right content ouside component scope to copy
    switch (context) {
      case 'reassessment-link':
        contentToCopy = await requestAppointmentLink('reassessment')
        break
      case 'ob-call-link':
        contentToCopy = await requestAppointmentLink('reschedule-onboarding')
        break
      case 'check-in-link':
        contentToCopy = await requestAppointmentLink('check_in_call')
        break
      default:
        console.warn('[patient-sidebar] Requested unsupported appointment type')
    }
  }

  if (contentToCopy) {
    try {
      if (shouldCopy) await copyToClipFn(String(contentToCopy))
      $notify.success(i18n.t('COPIED.CLIPBOARD'))
    } catch (er) {
      $notify.error(i18n.t('copy_89'))
    }

    return
  }
  console.warn('[patient-sidebar] Coulnd\'t get any content to copy')
}

const sendToUser = async ({ context }: {context: Context}) => {
  try {
    switch (context) {
      case 'reassessment-link':
        await requestAppointmentLink('reassessment', true)
        break
      case 'check-in-link':
        await requestAppointmentLink('check_in_call', true)
        break
      default:
        console.warn('[patient-sidebar] Requested unsupported appointment type')
        throw Error('Unsupported appointment type')
    }
    const member = `${memberProfile.value?.firstname} ${memberProfile.value?.lastname}`

    $notify.success(i18n.t('APPOINTMENT_LINK.LINK_SENT_TO_MEMBER', { member }))

    return Promise.resolve()
  } catch (e) {
    return Promise.reject(e)
  }
}

const getBaseSegmentEventProperties = () => {
  return {
    masterTaskId: `${masterTaskId.value}`,
    taskList: taskListSegmentEvent.value,
  }
}

const handleLeaveFeedEvent = ({ source, destination }: {source: FeedEventSource, destination: string}) => {
  const eventProperties = {
    ...getBaseSegmentEventProperties(),
    source,
    destination,
    programUuid: memberUuid.value,
    memberStatus: memberStatus.value,
  }

  segment.trackEventWithUser(EVENT_KEYS.LEAVE_FEED, eventProperties)
}

const handleStartCallEvent = ({ source }: {source: FeedEventSource}) => {
  const eventProperties = {
    ...getBaseSegmentEventProperties(),
    source,
  }

  segment.trackEventWithUser(EVENT_KEYS.CALL_STARTED, eventProperties)
}

const handleSidebarOpenedSegmentEvent = () => {
  segment.trackEventWithUser(EVENT_KEYS.MEMBER_SIDEBAR_OPENED, {
    masterTaskId: `${masterTaskId.value}`,
  })
}

watch(() => props.modalOpen, (isOpen) => {
  if (!isOpen || isNotesToggled.value) return
  handleSidebarOpenedSegmentEvent()
}, { immediate: true })

watch(error, (isError) => {
  if (!isError) return
  closeModal()
  $notify.error(i18n.t('copy_89'))
})

onClickOutside(drawerRef, (event) => {
  autoHide.value = false

  const el = event.target

  if (el instanceof HTMLElement && el.closest('.global-modal-container')) {
    return
  }

  autoHide.value = true
}, { ignore: [] })

const isMainLayout = computed(() => route.matched.some((r) => r.components?.default?.name === 'AppMain'))

return (_ctx: any,_cache: any) => {
  const _component_sword_loader = _resolveComponent("sword-loader")!
  const _component_x_drawer = _resolveComponent("x-drawer")!

  return (_openBlock(), _createBlock(_component_x_drawer, {
    "model-value": __props.modalOpen,
    position: "right",
    width: "450",
    inset: { top: isMainLayout.value ? 56 : 0 },
    "onUpdate:modelValue": closeModal
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "drawerRef",
        ref: drawerRef,
        class: "flex h-full items-center justify-center"
      }, [
        (_unref(isLoading) || !_unref(memberDrawerData))
          ? (_openBlock(), _createBlock(_component_sword_loader, { key: 0 }))
          : (_openBlock(), _createBlock(MemberDrawerContent, {
              key: 1,
              eligibility: _unref(memberDrawerData).insurance_info.eligibility,
              "care-coordinators": _unref(memberDrawerData).care_coordinators,
              "bloom-protocol": _unref(memberDrawerData).protocol,
              "profile-info": _unref(memberDrawerData).profile,
              "clinical-info": _unref(memberDrawerData).clinical,
              reminders: _unref(memberDrawerData).reminders,
              goals: _unref(memberDrawerData).goals,
              "therapist-id": therapistId.value,
              protocol: protocolInfo.value,
              "app-version": _unref(memberDrawerData).app?.version,
              age: age.value,
              "current-state": _unref(memberDrawerData)?.profile.current_state,
              "kit-history": _unref(memberDrawerData).kit_history,
              contacts: contacts.value,
              onCloseSideBar: closeModal,
              onOpenUi: openUi,
              onChangeAttentionLevel: changeAttentionLevel,
              onCopyToClipboard: copyToClipboard,
              onSendToUser: sendToUser,
              onLeaveFeed: handleLeaveFeedEvent,
              onStartCall: handleStartCallEvent
            }, null, 8, ["eligibility", "care-coordinators", "bloom-protocol", "profile-info", "clinical-info", "reminders", "goals", "therapist-id", "protocol", "app-version", "age", "current-state", "kit-history", "contacts"]))
      ], 512)
    ], undefined, true),
    _: 1
  }, 8, ["model-value", "inset"]))
}
}

})